import { Params } from '@angular/router';
import { AccountType } from '../../constants/account-types';

/**
 * CONF-001 — what a signed-in user is told when a route needs a role they do not have.
 *
 * `GlobalAuthGuard` used to send every such user to `login-router-st` with nothing attached. A
 * Scoring Leader who followed "Edit scoring window in Test Controller" (opens in a new tab) landed
 * on the educator / student sign-in choices with no hint that the page needs a Test Controller
 * account, and no way back to the page they had asked for.
 *
 * The guard now carries two query params to the sign-in page:
 *   - `access_denied` — the role(s) the route needs (account-type ids, comma separated)
 *   - `return_url`    — the page that was asked for
 *
 * The sign-in page names the role and, after a successful sign-in, returns to `return_url`. The
 * route guard still runs on arrival, so returning never grants anything: a user who signs in
 * without the role is explained to again.
 *
 * Pure (no Angular services) so the rules are unit tested directly.
 */

export const ACCESS_DENIED_QUERY = 'access_denied';
export const RETURN_URL_QUERY = 'return_url';

/**
 * Only same-origin, in-app paths may be returned to. Anything that could leave the app — a scheme,
 * a protocol-relative `//host`, a backslash that browsers read as `/` — is dropped, so a crafted
 * `?return_url=` on the sign-in page cannot become an open redirect.
 */
export const safeReturnUrl = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const url = raw.trim();
  if (!url.startsWith('/')) return null;
  if (url.startsWith('//') || url.includes('\\')) return null;
  if (/[\u0000-\u001f]/.test(url)) return null;
  return url;
};

export const accessDeniedQueryParams = (allowedRoles: string[], attemptedUrl: string): Params => {
  const params: Params = {};
  const roles = Array.from(new Set((allowedRoles || []).filter(Boolean)));
  if (roles.length) params[ACCESS_DENIED_QUERY] = roles.join(',');
  const returnUrl = safeReturnUrl(attemptedUrl);
  if (returnUrl) params[RETURN_URL_QUERY] = returnUrl;
  return params;
};

/**
 * Same test `GlobalAuthGuard` applies on arrival: does the signed-in user hold at least one of the
 * roles a route accepts? Lets a link check *before* opening a new tab, so a user without the role
 * gets a popup on the page they are on instead of landing on the sign-in page.
 */
export const hasAnyRole = (
  accountTypes: Array<{ account_type: string }> | null | undefined,
  allowedRoles: string[],
): boolean =>
  (accountTypes || []).some(record => (allowedRoles || []).includes(record.account_type));

export interface AccessDenied {
  roles: string[];
  returnUrl: string | null;
}

/** Reads the guard's params back. `null` when the visit was not an access refusal. */
export const parseAccessDenied = (params: Params | null | undefined): AccessDenied | null => {
  const rolesRaw = params ? params[ACCESS_DENIED_QUERY] : null;
  if (typeof rolesRaw !== 'string' || !rolesRaw.trim()) return null;
  const roles = Array.from(new Set(rolesRaw.split(',').map(r => r.trim()).filter(Boolean)));
  return { roles, returnUrl: safeReturnUrl(params[RETURN_URL_QUERY]) };
};

/**
 * Translation slug that names each role to the reader. Both Test Controller account types read as
 * "Test Controller" — the Data Exporter distinction is not something the reader can act on here.
 * A role with no entry is shown as its id rather than hidden.
 */
const ROLE_LABEL_SLUGS: { [role: string]: string } = {
  [AccountType.TEST_CTRL]: 'title_test_ctrl',
  [AccountType.TEST_CTRLD]: 'title_test_ctrl',
  [AccountType.SCOR_LEAD]: 'lbl_scoring_leader',
};

/** `translate` returns the slug itself when it has no row, so an untranslated slug falls back to the id. */
export const accessDeniedRoleLabel = (roles: string[], translate: (slug: string) => string): string => {
  const labels = roles.map(role => {
    const slug = ROLE_LABEL_SLUGS[role];
    if (!slug) return role;
    const text = translate(slug);
    return text && text !== slug ? text : role;
  });
  return Array.from(new Set(labels)).join(', ');
};

export interface AccessDeniedCopy {
  notice: string;
  next: string;
  requestedPage: string;
}

/**
 * Slugs for the explanation, each with EN/FR fallback copy so the page works before the rows land
 * in `data/translations.json` (see ref/pending-translations.yaml). `{{role}}` is filled by the caller.
 */
export const ACCESS_DENIED_COPY = {
  notice: {
    slug: 'txt_access_denied_notice',
    en: 'Your current account does not have access to this page. Required role: {{role}}.',
    fr: 'Votre compte actuel n\'a pas accès à cette page. Rôle requis : {{role}}.',
  },
  next: {
    slug: 'txt_access_denied_next',
    en: 'Sign in with an account that has this role to continue to the page you asked for.',
    fr: 'Connectez-vous avec un compte qui possède ce rôle pour accéder à la page demandée.',
  },
  requestedPage: {
    slug: 'lbl_access_denied_requested_page',
    en: 'Page requested',
    fr: 'Page demandée',
  },
};

export const accessDeniedCopy = (
  roleLabel: string,
  isFrench: boolean,
  translate: (slug: string) => string,
): AccessDeniedCopy => {
  const pick = (entry: { slug: string; en: string; fr: string }) => {
    const text = translate(entry.slug);
    return text && text !== entry.slug ? text : (isFrench ? entry.fr : entry.en);
  };
  return {
    notice: pick(ACCESS_DENIED_COPY.notice).replace('{{role}}', roleLabel),
    next: pick(ACCESS_DENIED_COPY.next),
    requestedPage: pick(ACCESS_DENIED_COPY.requestedPage),
  };
};
